// src/components/rooms/RoomDetailModal.jsx
import React from 'react';
import { 
  FiX, FiEdit2, FiTrash2, FiUsers, FiMapPin, FiClock, 
  FiSettings, FiCalendar, FiInfo, FiWifi, FiMonitor 
} from 'react-icons/fi';

const getStatusColor = (status) => {
  switch (status) {
    case 'available': return 'bg-green-100 text-green-800 border-green-200';
    case 'occupied': return 'bg-red-100 text-red-800 border-red-200';
    case 'maintenance': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getTypeIcon = (type) => {
  switch (type) {
    case 'treatment': return <FiMapPin className="h-5 w-5" />;
    case 'consultation': return <FiUsers className="h-5 w-5" />;
    case 'group': return <FiUsers className="h-5 w-5" />;
    case 'massage': return <FiClock className="h-5 w-5" />;
    default: return <FiMapPin className="h-5 w-5" />;
  }
};

const getEquipmentIcon = (equipment) => {
  const item = equipment.toLowerCase();
  if (item.includes('wifi') || item.includes('internet')) return <FiWifi className="h-4 w-4" />;
  if (item.includes('monitor') || item.includes('screen') || item.includes('tv')) return <FiMonitor className="h-4 w-4" />;
  if (item.includes('calendar') || item.includes('schedule')) return <FiCalendar className="h-4 w-4" />;
  return <FiSettings className="h-4 w-4" />;
};

export default function RoomDetailModal({ room, onClose, onEdit, onDelete }) {
  const handleEdit = () => {
    onEdit(room);
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm(`Delete room "${room.name}"? This action cannot be undone.`)) {
      onDelete(room.id);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-lg bg-blue-500 bg-opacity-10">
                {getTypeIcon(room.type)}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{room.name}</h1>
                <p className="text-gray-600 capitalize">{room.type || 'General'} Room</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getStatusColor(room.status)}`}>
                {room.status || 'available'}
              </span>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FiUsers className="h-5 w-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Capacity</h3>
                </div>
                <p className="text-2xl font-bold text-blue-600">{room.capacity} people</p>
                <p className="text-sm text-gray-600 mt-1">Maximum occupancy</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FiMapPin className="h-5 w-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Location</h3>
                </div>
                <p className="text-lg font-medium text-gray-900">{room.location || 'Not specified'}</p>
                <p className="text-sm text-gray-600 mt-1">Room location</p>
              </div>
            </div>

            {/* Description */}
            {room.description && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <FiInfo className="h-5 w-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Description</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">{room.description}</p>
              </div>
            )}

            {/* Equipment */}
            {room.equipment && Array.isArray(room.equipment) && room.equipment.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <FiSettings className="h-5 w-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Equipment & Amenities</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {room.equipment.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="p-2 rounded-lg bg-blue-500 bg-opacity-10">
                        {getEquipmentIcon(item)}
                      </div>
                      <span className="text-gray-900 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Details */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Room Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Room ID:</span>
                  <span className="font-medium text-gray-900">{room.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium text-gray-900 capitalize">{room.type || 'General'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(room.status)}`}>
                    {room.status || 'available'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="font-medium text-gray-900">
                    {room.updatedAt ? new Date(room.updatedAt).toLocaleDateString() : 'Recently'}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {room.notes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <FiInfo className="h-5 w-5 text-yellow-600" />
                  <h3 className="text-lg font-semibold text-yellow-800">Notes</h3>
                </div>
                <p className="text-yellow-700">{room.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Click outside or press ESC to close
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleEdit}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FiEdit2 className="h-4 w-4" />
                <span>Edit Room</span>
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <FiTrash2 className="h-4 w-4" />
                <span>Delete Room</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
